import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchMyListings, deleteMarketplaceListing, type MarketplaceListing } from '../lib/marketplace-api';
import { formatPrice, categoryLabel, CONDITION_LABELS } from '../lib/marketplace-categories';
import { isLoggedIn, getUser, logout } from '../lib/marketplace-auth';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  sold: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
  paused: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
};

export default function MarketplaceDashboard() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);
  const user = getUser();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/marketplace/signin', { replace: true });
      return;
    }
    fetchMyListings()
      .then(setListings)
      .catch((err) => {
        if (String(err.message).includes('authenticated') || String(err.message).includes('401')) {
          logout();
          navigate('/marketplace/signin', { replace: true });
        } else {
          setError(err.message || 'Failed to load listings');
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleDelete = async (listing: MarketplaceListing) => {
    if (!window.confirm(`Delete "${listing.title}"? This can't be undone.`)) return;
    setDeleting(listing.id);
    try {
      await deleteMarketplaceListing(listing.id);
      setListings((ls) => ls.filter((l) => l.id !== listing.id));
    } catch (err: any) {
      alert(err.message || 'Failed to delete listing');
    } finally {
      setDeleting(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/marketplace');
  };

  return (
    <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/marketplace" className="text-text-secondary hover:text-accent transition-colors">Marketplace</Link>
        <span>/</span>
        <span>My listings</span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl md:text-[1.8rem] font-bold">My listings</h1>
          <p className="text-text-secondary text-sm mt-1">
            Signed in as {user?.name || user?.email}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/marketplace/create" className="btn btn-primary btn-sm">
            + New listing
          </Link>
          <button onClick={handleLogout} className="btn btn-ghost btn-sm">
            Sign out
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-6 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg animate-pulse" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-20 bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg">
          <div className="text-5xl mb-4">🛥️</div>
          <h2 className="font-display text-xl font-bold mb-2">No listings yet</h2>
          <p className="text-text-secondary text-sm mb-6">List your first item - it takes less than a minute.</p>
          <Link to="/marketplace/create" className="btn btn-primary">Create a listing</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map((listing) => (
            <div
              key={listing.id}
              className="bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-lg p-4 flex flex-col sm:flex-row gap-4"
            >
              {/* Thumb */}
              <Link to={`/marketplace/${listing.slug}`} className="flex-shrink-0">
                {listing.images?.[0] ? (
                  <img
                    src={listing.images[0]}
                    alt={listing.title}
                    className="w-full sm:w-28 h-32 sm:h-20 object-cover rounded-md"
                  />
                ) : (
                  <div className="w-full sm:w-28 h-32 sm:h-20 bg-surface-light dark:bg-surface rounded-md flex items-center justify-center text-text-muted">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="m21 15-5-5L5 21" />
                    </svg>
                  </div>
                )}
              </Link>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/marketplace/${listing.slug}`}
                      className="font-semibold text-sm hover:text-accent transition-colors line-clamp-1"
                    >
                      {listing.title}
                    </Link>
                    <div className="text-text-muted text-xs mt-1">
                      {categoryLabel(listing.category)} · {CONDITION_LABELS[listing.condition] || listing.condition} · {listing.region}
                    </div>
                  </div>
                  <div className="font-display font-bold text-sm whitespace-nowrap">
                    {formatPrice(listing.price, listing.price_type)}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
                  <span className={`px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase tracking-wide border ${STATUS_STYLES[listing.status || 'active'] || STATUS_STYLES.active}`}>
                    {listing.status || 'active'}
                  </span>
                  <span className="text-text-muted text-xs">
                    {listing.views ?? 0} view{(listing.views ?? 0) === 1 ? '' : 's'}
                  </span>
                  <span className="text-text-muted text-xs">
                    {listing.inquiries ?? 0} inquir{(listing.inquiries ?? 0) === 1 ? 'y' : 'ies'}
                  </span>
                  <span className="text-text-muted text-xs">
                    Listed {new Date(listing.created_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                  </span>
                  <div className="flex gap-3 ml-auto">
                    <Link
                      to={`/marketplace/${listing.slug}`}
                      className="text-xs font-medium text-accent hover:underline"
                    >
                      View
                    </Link>
                    <button
                      onClick={() => handleDelete(listing)}
                      disabled={deleting === listing.id}
                      className="text-xs font-medium text-red-500 hover:underline disabled:opacity-50"
                    >
                      {deleting === listing.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
